import React from 'react';
import { Package, Truck, MapPin, CheckCircle, XCircle, Clock, Box } from 'lucide-react';

function StatisticsCards({ deliveries, deliveriesProvince, orders }) {
  // Lima deliveries statistics
  const limaTotal = deliveries.length;
  const limaEntregado = deliveries.filter(d => d.status === 'entregado').length;
  const limaRechazado = deliveries.filter(d => d.status === 'rechazado').length;
  const limaPending = deliveries.filter(d => d.status === 'pending').length;
  const limaInTransit = deliveries.filter(d => d.status === 'in_transit').length;
  const limaDropship = deliveries.filter(d => d.provider_id !== null && d.provider_id !== undefined).length;

  // Provincia deliveries statistics
  const provinciaTotal = deliveriesProvince.length;
  const provinciaEntregado = deliveriesProvince.filter(d => d.status === 'entregado').length;
  const provinciaRechazado = deliveriesProvince.filter(d => d.status === 'rechazado').length;
  const provinciaPending = deliveriesProvince.filter(d => d.status === 'pending').length;
  const provinciaInTransit = deliveriesProvince.filter(d => d.status === 'in_transit').length;
  const provinciaDropship = deliveriesProvince.filter(d => d.provider_id !== null && d.provider_id !== undefined).length;

  // Combined statistics
  const totalDeliveries = limaTotal + provinciaTotal;
  const totalEntregado = limaEntregado + provinciaEntregado;
  const totalRechazado = limaRechazado + provinciaRechazado;
  const totalPending = limaPending + provinciaPending;
  const totalInTransit = limaInTransit + provinciaInTransit;
  const totalDropship = limaDropship + provinciaDropship;

  // Pickups
  const totalPickups = orders.length;
  const pendingPickups = orders.filter(o => o.status === 'pending').length;
  const completedPickups = orders.filter(o => o.status === 'completed').length;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Total Deliveries */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between mb-3">
          <div className="w-10 h-10 bg-[#B1336E]/10 rounded-lg flex items-center justify-center">
            <Package className="w-5 h-5 text-[#B1336E]" />
          </div>
          <span className="text-2xl font-extrabold text-slate-900 font-montserrat">
            {totalDeliveries}
          </span>
        </div>
        <h3 className="text-sm font-semibold text-slate-700 font-montserrat mb-2">
          Total Entregas
        </h3>
        <div className="flex gap-2 text-xs text-slate-600 font-montserrat">
          {limaTotal > 0 && (
            <span className="bg-[#B1336E]/10 px-2 py-1 rounded">
              Lima: {limaTotal}
            </span>
          )}
          {provinciaTotal > 0 && (
            <span className="bg-[#E6B43C]/10 px-2 py-1 rounded">
              Prov: {provinciaTotal}
            </span>
          )}
        </div>
      </div>

      {/* Delivered */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between mb-3">
          <div className="w-10 h-10 bg-[#67A43C]/10 rounded-lg flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-[#67A43C]" />
          </div>
          <span className="text-2xl font-extrabold text-slate-900 font-montserrat">
            {totalEntregado}
          </span>
        </div>
        <h3 className="text-sm font-semibold text-slate-700 font-montserrat mb-2">
          Entregados
        </h3>
        <div className="flex gap-2 text-xs text-slate-600 font-montserrat">
          {limaEntregado > 0 && (
            <span className="bg-[#67A43C]/10 px-2 py-1 rounded">
              Lima: {limaEntregado}
            </span>
          )}
          {provinciaEntregado > 0 && (
            <span className="bg-[#67A43C]/10 px-2 py-1 rounded">
              Prov: {provinciaEntregado}
            </span>
          )}
        </div>
      </div>

      {/* Rejected */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between mb-3">
          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
            <XCircle className="w-5 h-5 text-red-600" />
          </div>
          <span className="text-2xl font-extrabold text-slate-900 font-montserrat">
            {totalRechazado}
          </span>
        </div>
        <h3 className="text-sm font-semibold text-slate-700 font-montserrat mb-2">
          Rechazados
        </h3>
        <div className="flex gap-2 text-xs text-slate-600 font-montserrat">
          {limaRechazado > 0 && (
            <span className="bg-red-100 px-2 py-1 rounded">
              Lima: {limaRechazado}
            </span>
          )}
          {provinciaRechazado > 0 && (
            <span className="bg-red-100 px-2 py-1 rounded">
              Prov: {provinciaRechazado}
            </span>
          )}
        </div>
      </div>

      {/* Pending + In Transit */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between mb-3">
          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
            <Clock className="w-5 h-5 text-amber-600" />
          </div>
          <span className="text-2xl font-extrabold text-slate-900 font-montserrat">
            {totalPending + totalInTransit}
          </span>
        </div>
        <h3 className="text-sm font-semibold text-slate-700 font-montserrat mb-2">
          En Proceso
        </h3>
        <div className="flex gap-2 text-xs text-slate-600 font-montserrat">
          <span className="bg-amber-100 px-2 py-1 rounded">
            Pendiente: {totalPending}
          </span>
          <span className="bg-blue-100 px-2 py-1 rounded">
            Tránsito: {totalInTransit}
          </span>
        </div>
      </div>

      {/* Dropship Deliveries */}
      {totalDropship > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Box className="w-5 h-5 text-purple-600" />
            </div>
            <span className="text-2xl font-extrabold text-slate-900 font-montserrat">
              {totalDropship}
            </span>
          </div>
          <h3 className="text-sm font-semibold text-slate-700 font-montserrat mb-2">
            Dropshipping
          </h3>
          <div className="flex gap-2 text-xs text-slate-600 font-montserrat">
            {limaDropship > 0 && (
              <span className="bg-purple-100 px-2 py-1 rounded">
                Lima: {limaDropship}
              </span>
            )}
            {provinciaDropship > 0 && (
              <span className="bg-purple-100 px-2 py-1 rounded">
                Prov: {provinciaDropship}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Pickups */}
      {totalPickups > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-[#E6B43C]/10 rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-[#E6B43C]" />
            </div>
            <span className="text-2xl font-extrabold text-slate-900 font-montserrat">
              {totalPickups}
            </span>
          </div>
          <h3 className="text-sm font-semibold text-slate-700 font-montserrat mb-2">
            Recojos
          </h3>
          <div className="flex gap-2 text-xs text-slate-600 font-montserrat">
            <span className="bg-[#E6B43C]/10 px-2 py-1 rounded">
              Pendiente: {pendingPickups}
            </span>
            <span className="bg-[#67A43C]/10 px-2 py-1 rounded">
              Completado: {completedPickups}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default StatisticsCards;